import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Box, Icon, PulsingOrb, Text } from '~/atomic';
import { extractContractRequest, handleApiError } from '~/services';
import modalStatusService from '~/services/modalStatus';
import { mapExtractionDraftToApp } from '~/store/contracts/mappers';
import { useAppDispatch } from '~/store';
import { pushRecentUpload } from '~/store/contracts/slices';
import { cn } from '~/utils/cn';
import type { ContractDraft } from '~/store/contracts/interfaces';

const STEPS = [
  'Enviando o contrato',
  'Lendo o documento',
  'Extraindo dados financeiros',
  'Montando o resumo',
];

const CREEP_DURATION_MS = 14000;
const CREEP_TARGET = 0.9;

interface FileMeta {
  name: string;
  size: number;
  type: string;
}

interface LocationState {
  file?: FileMeta;
}

function isEmptyDraft(draft: ContractDraft): boolean {
  return !(
    draft.institution?.id ||
    draft.contractNumber ||
    draft.amountFinanced ||
    draft.installmentsTotal ||
    draft.startDate ||
    draft.endDate ||
    draft.installmentSchedule?.length
  );
}

function StepRow({ label, done, active }: { label: string; done: boolean; active: boolean }) {
  return (
    <div className="flex items-center gap-smaller py-quark">
      {done ? (
        <div className="w-[24px] h-[24px] rounded-full bg-success-03 flex items-center justify-center">
          <Icon name="icon-check" size={14} color="#FFFFFF" rounded={false} />
        </div>
      ) : (
        <div
          className={cn(
            'w-[24px] h-[24px] rounded-full border-2',
            active ? 'border-intense-01' : 'border-intense-01/30',
          )}
        />
      )}
      <Text
        className={
          done || active
            ? 'text-paragraphMD color-intense-01'
            : 'text-paragraphMD color-intense-04'
        }
      >
        {label}
      </Text>
    </div>
  );
}

export default function ContractAnalyzingView() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;

  const [progress, setProgress] = useState(0);
  const navigatedRef = useRef(false);
  const startedAt = useRef(Date.now());

  const file = state.file;
  const fileLabel = useMemo(() => {
    if (file) {
      const sizeMb = file.size / 1024 / 1024;
      return `${file.name} · ${sizeMb.toFixed(1).replace('.', ',')} MB`;
    }
    return 'Validando dados informados';
  }, [file]);

  const currentStep = Math.min(STEPS.length - 1, Math.floor(progress / (1 / STEPS.length)));

  useEffect(() => {
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      const elapsed = Date.now() - startedAt.current;
      const t = Math.min(1, elapsed / CREEP_DURATION_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      setProgress((prev) => {
        const next = eased * CREEP_TARGET;
        return next > prev && prev < CREEP_TARGET ? next : prev;
      });
      if (t < 1 && !navigatedRef.current) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    const finish = (draft: ContractDraft | null) => {
      if (cancelled || navigatedRef.current) return;
      navigatedRef.current = true;
      setProgress(1);
      setTimeout(() => {
        navigate('/app/contracts/review', {
          replace: true,
          state: { mode: 'ai', draft: draft ?? {} },
        });
      }, 400);
    };

    const actualFile = (window as unknown as { __pagaagroPendingFile?: File })
      .__pagaagroPendingFile;

    if (!actualFile) {
      finish({});
      return;
    }

    (async () => {
      try {
        const { data } = await extractContractRequest(actualFile);
        if (cancelled) return;
        const draft = mapExtractionDraftToApp(data.draft);
        const extracted = data.status !== 'failed' && !isEmptyDraft(draft);

        dispatch(
          pushRecentUpload({
            id: data.extractionId,
            fileName: actualFile.name,
            sizeBytes: actualFile.size,
            uploadedAt: new Date().toISOString(),
            status: extracted ? 'processed' : 'failed',
          }),
        );

        if (extracted) {
          finish(draft);
          return;
        }

        modalStatusService.show({
          type: 'alert',
          title: 'Não conseguimos ler o documento',
          description:
            'O arquivo foi enviado, mas não identificamos os dados do contrato. Confira se o PDF não é uma imagem de baixa qualidade — ou preencha os campos manualmente.',
          showFirstButton: true,
          textFirstButton: 'Preencher manualmente',
          onClickFirstButton: () => modalStatusService.hide(),
          handleCloseModal: () => modalStatusService.hide(),
        });
        finish(draft);
      } catch (error) {
        if (cancelled) return;
        handleApiError(error, {
          title: 'Não foi possível analisar',
          fallbackDescription: 'Tente reenviar o contrato em alguns instantes.',
        });
        navigate('/app/add', { replace: true });
      } finally {
        (window as unknown as { __pagaagroPendingFile?: File }).__pagaagroPendingFile = undefined;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate]);

  const handleClose = () => navigate(-1);

  return (
    <div className="min-h-screen flex flex-col text-intense-01 relative overflow-hidden safe-area-top safe-area-bottom">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, #163826 0%, #1d4733 50%, #123524 100%)',
        }}
      />
      <Box className="relative px-small pt-small">
        <button
          type="button"
          onClick={handleClose}
          className="w-[40px] h-[40px] rounded-[12px] bg-intense-01/15 flex items-center justify-center hover:bg-intense-01/25"
        >
          <Icon name="icon-close" size={20} color="#FFFFFF" rounded={false} />
        </button>
      </Box>

      <Box className="relative flex-1 flex flex-col items-center px-small w-full xl:max-w-[480px] mx-auto pb-large">
        <Box className="mt-larger flex items-center justify-center">
          <PulsingOrb color="#FFFFFF" size={140} />
        </Box>

        <Text className="mt-larger text-display1 color-intense-01 text-center" as="p">
          Analisando o contrato
        </Text>
        <Text className="mt-quark text-bodySM color-intense-04 text-center">
          {fileLabel}
        </Text>

        <div className="w-full mt-larger h-[6px] bg-intense-01/20 rounded-full overflow-hidden">
          <div
            className="h-full bg-intense-01 rounded-full transition-all duration-200 ease-out"
            style={{ width: `${progress * 100}%` }}
          />
        </div>

        <div className="w-full mt-larger rounded-medium bg-intense-07/40 p-small flex flex-col gap-quark">
          {STEPS.map((label, idx) => (
            <StepRow
              key={label}
              label={label}
              done={progress >= 1 || idx < currentStep}
              active={idx === currentStep && progress < 1}
            />
          ))}
        </div>
      </Box>
    </div>
  );
}
